import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { CallerService } from '../services/caller.service'
import { MatDialog } from '@angular/material/dialog'
import { HttpResponse, HttpStatusCode } from '@angular/common/http'
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  tap,
  catchError,
  takeUntil,
} from 'rxjs/operators'
import { Observable, of, Subject } from 'rxjs'
import { PpaModel, SaleType, AccommodationType } from '../ngrx-store/app.state'
import { MoreViewSearchComponent } from '../dialogs/more-view-search/more-view-search.component'
import { BreakpointObserver } from '@angular/cdk/layout'
import { PPA_PHONE_QUERY, PpaView, ppaListClass, readPpaView } from '../utils/ppa.util'

const SEARCH_DEBOUNCE_MS = 1000

@Component({
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.css'],
  // `results`/`isSearching`/`isFetchingResults` are mutated inside RxJS `.subscribe()`
  // callbacks rather than pushed through the template via `async`, so OnPush won't pick up
  // those changes on its own - each mutation site below calls `cdr.markForCheck()` right after
  // to flag the view dirty. This is the low-diff way to get OnPush's win (skip this component
  // on unrelated app-wide change detection ticks) without rewriting the template's bindings.
  // @todo: the more thorough fix is converting this component's state to observables bound via
  // the `async` pipe throughout the template - that removes the need for markForCheck() calls
  // entirely, but touches most of the bindings in search-results.component.html.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultsComponent implements OnInit, OnChanges, OnDestroy {
  /** Prefills (and runs) the search - e.g. when opened as a dialog with a starting query. */
  @Input() initialSearchText: string | null = null

  /** Prefills the state filter, e.g. 'LAGOS'. */
  @Input() initialState: string | null = null

  /** Prefills the LGA filter, e.g. 'Aba North' - needs initialState too. */
  @Input() initialLga: string | null = null

  /** Raw 'tab' query param value (e.g. 'sales', 'accommodation', 'corps-members', 'ppas'). */
  @Input() initialTab: string | null = null

  private readonly destroy$ = new Subject<void>()

  initialResults = {
    sales: [],
    corp_members: [],
    ppas: [],
    accommodations: [],
  }
  results: {
    sales: Array<any>
    corp_members: Array<any>
    accommodations: Array<any>
    ppas: Array<any>
  } = this.initialResults

  /**
   * The currently selected tab index of search results
   * 0: Sales, 2: Corp Members, 3: Accommodations, 3: PPAs
   */
  public readonly SEARCH_TAB_INDEXES = {
    SALES: 0,
    CORP_MEMBERS: 2,
    ACCOMMODATIONS: 1,
    PPAs: 3,
  }

  /* The currently selected tab index of search results */
  selectedTabIndex = this.SEARCH_TAB_INDEXES.SALES

  isFetchingResults: boolean = false

  /** PPAs are laid out as on /ppa: compact rows on a phone, else the list or grid chosen there. */
  isPhone = false
  readonly ppaView: PpaView = readPpaView()
  readonly ppaListClass = ppaListClass

  constructor(
    public callerService: CallerService,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private breakpointObserver: BreakpointObserver,
  ) {}

  filteredStates: Observable<string[]> | undefined
  filteredLGAs: Observable<string[]> | undefined

  lgaOptions: string[] = []

  isSearching = false

  searchForm: FormGroup = new FormGroup({
    searchText: new FormControl('', []),
    state: new FormControl('', []),
    lga: new FormControl('', []),
  })

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialTab']) {
      this.selectedTabIndex = this.tabIndexFromTabParam(this.initialTab)
    }
    // Later query param changes on the same page (ngOnInit handles the first values).
    const prefillChanged = changes['initialSearchText'] ?? changes['initialState'] ?? changes['initialLga']
    if (prefillChanged && !prefillChanged.firstChange) {
      this.applyInitialSearch()
    }
  }

  /** Runs after the valueChanges subscription exists, so the prefilled search actually runs. */
  private applyInitialSearch(): void {
    if (!this.initialSearchText && !this.initialState) {
      return
    }
    this.searchForm.patchValue({
      ...(this.initialSearchText && { searchText: this.initialSearchText }),
      ...(this.initialState && { state: this.initialState }),
      ...(this.initialState && this.initialLga && { lga: this.initialLga }),
    })
  }

  ngOnInit() {
    this.breakpointObserver.observe(PPA_PHONE_QUERY).pipe(takeUntil(this.destroy$)).subscribe(({ matches }) => {
      this.isPhone = matches
      this.cdr.markForCheck()
    })

    // @todo: this fetches every sale/accommodation/corp member/ppa on every open - needs a
    // backend change (a paginated/"trending" endpoint) rather than a frontend fix.
    // Skipped when opening with a search, so it can't land after (and overwrite) those results.
    if (!this.initialSearchText && !this.initialState) {
      this.isFetchingResults = true
      this.callerService
        .getAllItems()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: HttpResponse<any>) => {
            if (res.status === HttpStatusCode.Ok) {
              this.results = res.body
            } // TODO: need else block?
            this.isFetchingResults = false
            this.cdr.markForCheck()
          },
          error: (err) => {
            this.callerService.showNotification('Failed to retrieve data')
            this.isFetchingResults = false
            this.cdr.markForCheck()
          },
        })
    }

    // Search when any of the inputs change.
    this.searchForm.valueChanges
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS), // Wait some ms after the user stops typing
        distinctUntilChanged(
          (_old, _new) =>
            _old.lga === _new.lga &&
            _old.searchText === _new.searchText &&
            _old.state === _new.state
        ), // Only emit if the current value is different than the last
        switchMap((searchFormValue) => {
          this.isSearching = true
          this.cdr.markForCheck()
          // Cancels the previous API call if a new value is emitted.
          return this.callerService.search(searchFormValue).pipe(
            // Without this, one failed search (e.g. a 500) kills this subscription for good -
            // RxJS unsubscribes the whole chain on an unhandled error, so every search after
            // that point silently does nothing.
            catchError((err) => {
              console.log('ERR searching', err)
              return of(null)
            })
          )
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res: HttpResponse<any> | null) => {
          if (res?.status === HttpStatusCode.Ok) {
            this.results = res.body
          }
          this.isSearching = false
          this.cdr.markForCheck()
        },
      })

    this.filteredStates = this.searchForm.get('state')?.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterStates(value || '')),
      tap((value) => {
        // update LGA options on state change
        if (value?.[0]) {
          // @ts-ignore
          this.lgaOptions = this.callerService.states_long_lgas?.[value?.[0]]
          // works, but why do we need to re-assign this again?
          this.filteredLGAs = this.searchForm.get('lga')?.valueChanges.pipe(
            startWith(''),
            map((value) => this._filterLGAs(value || ''))
          )
        }
      }),
      takeUntil(this.destroy$),
    )

    this.filteredLGAs = this.searchForm.get('lga')?.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterLGAs(value || '')),
      takeUntil(this.destroy$),
    )

    this.applyInitialSearch()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private tabIndexFromTabParam(tab: string | null): number {
    switch (tab) {
      case 'accommodation':
        return this.SEARCH_TAB_INDEXES.ACCOMMODATIONS
      case 'corps-members':
        return this.SEARCH_TAB_INDEXES.CORP_MEMBERS
      case 'ppas':
        return this.SEARCH_TAB_INDEXES.PPAs
      case 'sales':
      default:
        return this.SEARCH_TAB_INDEXES.SALES
    }
  }

  /**
   * Happens even when the input changes.
   * @param event selectionChange
   */
  lgaChanged(event: any) {
    const { value: lga } = event.target
    console.log('lga changed?', lga)
  }

  private _filterStates(value: string): string[] {
    const filterValue = value.toLowerCase()
    return this.callerService.states_long.filter((option) =>
      option.toLowerCase().includes(filterValue)
    )
  }

  private _filterLGAs(value: string): string[] {
    const filterValue = value.toLowerCase()

    // TODO: We should ideally only update until one state is selected
    // @ts-ignore
    this.lgaOptions =
      // @ts-ignore
      this.callerService.states_long_lgas[
        this.searchForm.get('state')?.value
      ] ?? []

    // @ts-ignore
    return this.lgaOptions?.filter((option: string) =>
      option.toLowerCase().includes(filterValue)
    )
  }

  clearSearchInput() {
    this.searchForm?.get('searchText')?.setValue('')
  }
  /**
   * Idea is when you change the state input, it should also reset the lga input
   */
  clearStateInput() {
    this.searchForm?.get('state')?.setValue('')
    this.clearLgaInput()
  }
  clearLgaInput() {
    this.searchForm?.get('lga')?.setValue('')
  }

  menuState = ''

  get getEmptyResultText(): string {
    if (this.isSearching) {
      return 'Searching...'
    }

    if (this.isFetchingResults) {
      return 'Fetching results...'
    }

    const searchText = this.searchForm?.get('searchText')?.value

    const currentTabText =
      this.selectedTabIndex === this.SEARCH_TAB_INDEXES.SALES
        ? 'Items for Sale'
        : this.selectedTabIndex === this.SEARCH_TAB_INDEXES.CORP_MEMBERS
        ? 'Corp Members'
        : this.selectedTabIndex === this.SEARCH_TAB_INDEXES.ACCOMMODATIONS
        ? 'Accommodations'
        : 'PPAs'

    if (searchText) {
      return `No ${currentTabText} found for "${searchText}"`
    }
    return `No ${currentTabText}`
  }

  toggleMenuState(): void {
    this.menuState = this.menuState == '' ? '_open' : ''
  }

  openMorePpaDetails(ppa: PpaModel | any) {
    try {
      const dialogRef = this.dialog.open(MoreViewSearchComponent, {
        maxWidth: '100dvw',
        maxHeight: '100dvh',
        height: '100%',
        width: '100%',
        data: {
          ppa,
        },
        ariaLabel: 'Dialog to show more details about a PPA',
        role: 'dialog',
      })

      /* record that this house was seen, probably record how long it was seen. tell the agent who posted it? */
      dialogRef.afterClosed().subscribe((result) => {
        console.log('The view more dialog was closed', result)
      })
    } catch (error) {
      console.log('err opening dialog to view more ppa details', error)
    }
  }

  trackById(_index: number, item: SaleType | AccommodationType | PpaModel | { id: number }): number {
    return item.id
  }
}
